import ArrowDownIcon from '@atlaskit/icon/core/arrow-down';
import ArrowUpIcon from '@atlaskit/icon/core/arrow-up';
import CopyIcon from '@atlaskit/icon/core/copy';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import DeleteIcon from '@atlaskit/icon/core/delete';
import LinkIcon from '@atlaskit/icon/core/link';
import { type TriggerProps } from '@atlaskit/popup';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { actionBarState } from '@databases/action-bar/action-bar-state';
import { actionBarPortalState } from '@databases/action-bar/selections-effect';
import {
  getDatabaseContainerType,
  SendDatabaseDeleteEntryEventSources,
  sendDeleteEntryEvent,
  useAnalytics,
} from '@databases/analytics';
import ContextualMenu, { type MenuItem } from '@databases/contextual-menu/ContextualMenu';
import { type MenuData } from '@databases/contextual-menu/contextual-menu-state';
import { useContextualMenu } from '@databases/contextual-menu/useContextualMenu';
import { copyToClipboard } from '@databases/copy-paste/copy-helper';
import {
  InlineRovoPromptTriggerSource,
  openInlineRovoPrompt,
} from '@databases/database-ai-first-creation-state/inline-rovo-prompt-state';
import { AskRovoIcon } from '@databases/database-ai-first-creation-ui/icons/ask-rovo-icon';
import { getBaseUrl } from '@databases/database-state';
import {
  ActiveVerticalDragHandlerIcon,
  CollapseIcon,
  ExpandIcon,
  InsertRowAboveIcon,
  InsertRowBelowIcon,
  VerticalDragHandlerIcon,
} from '@databases/icons';
import { messages } from '@databases/intl/messages';
import { SHORTCUTS } from '@databases/keyboard-shortcuts';
import usePanel from '@databases/panel/usePanel';
import usePanelState from '@databases/panel/usePanelState';
import {
  type HoverProps,
  isModifierKeyPressed,
  useComponent,
  useKeyboardShortcut,
} from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  doClearEntireEntries,
  doDeleteEntries,
  doDuplicateEntireEntries,
  doInsertEntries,
  doMoveEntries,
  doSelectNewEntries,
} from '@databases/redux/contextual-menu/actions';
import { getService } from '@databases/service-container';
import { IGNORED_TABLE_ELEMENT_CLASS } from '@databases/table-resizer';
import { DatabaseLayoutType } from '@databases/types/database/view';
import { TABLE_CLASSNAMES } from '@databases/utils/constants';
import { getIsFilterOrSortingActive } from '@databases/utils/database/view/view';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import {
  getDatabaseLinkUrlWithQueryParams,
  getDatabasePath,
} from '@databases/utils/urls';
import useView from '@databases/views/useView';
import React, {
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';
import { useSnapshot } from 'valtio';

import { getExperimentValue } from '@canvas/client-feature-flags';

import {
  DragHandlerWrapper,
  InlineSelectButtonContainer,
  RowSelectButtonContainer,
} from './styles';
import { Position } from './type';

enum SELECT_ENTRY_MENU_ACTIONS {
  ASK_ROVO = 'ask_rovo',
  EXPAND_ENTRY = 'expand_entry',
  COLLAPSE_ENTRY = 'collapse_entry',
  COPY_ENTRY_LINK = 'copy_entry_link',
  ADD_ENTRY_ABOVE = 'add_entry_above',
  ADD_ENTRY_BELOW = 'add_entry_below',
  DUPLICATE_ENTRIES = 'duplicate_entries',
  CLEAR_CELLS = 'clear_cells',
  DELETE_ENTRIES = 'delete_entries',
  MOVE_ENTRIES_UP = 'move_entries_up',
  MOVE_ENTRIES_DOWN = 'move_entries_down',
}

type SelectEntryMenuProps = {
  contentId: string;
  entryId: string;
  currentViewId?: string;
  hoverProps?: HoverProps;
  handleSelectEntry: (e: React.MouseEvent | React.KeyboardEvent) => void;
  canEditContent: boolean;
  trigger?: (triggerProps: TriggerProps) => React.ReactNode;
  inlineTrigger?: boolean;
};

const SelectEntryMenu: FunctionComponent<SelectEntryMenuProps> = ({
  contentId,
  entryId,
  currentViewId,
  hoverProps,
  handleSelectEntry,
  canEditContent,
  trigger,
  inlineTrigger = false,
}) => {
  const {
    view: { settings: viewSettings },
  } = useView();
  const isDBAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const baseUrl = getBaseUrl();
  const store = defaultStoreUtils.useStore();
  const database = selectDatabaseByContentIdRequired(store.getState(), {
    contentId,
  });
  const databasePath = getDatabasePath({
    spaceKey: database.space.key,
    contentId,
  });
  const selectionsReader = getService('SelectionsReader');
  const isSelectionsPerformanceEnabled = getExperimentValue(
    'databasesSelectionsPerformance',
    'isEnabled',
    { fireExperimentExposure: false }
  );
  const actionBar = useSnapshot(actionBarState.state);
  const actionBarPortal = useSnapshot(actionBarPortalState);
  const isActionBarEnabled = isSelectionsPerformanceEnabled
    ? actionBarPortal.isVisible
    : actionBar.isActionBarEnabled;

  // Guard heavier reads behind isSelected so unselected entries skip them.
  const isSelected = selectionsReader.isEntrySelected(entryId);
  const selectedEntireEntryIds = isSelected ? selectionsReader.getSelectedEntryIds() : [];
  const oneSelectedEntry =
    selectedEntireEntryIds.length > 1 ? null : selectedEntireEntryIds[0];
  const selectedEntriesCount = selectedEntireEntryIds.length;
  const hasEntryBeforeSelection =
    isSelected && selectionsReader.hasEntryBeforeSelection();
  const hasEntryAfterSelection = isSelected && selectionsReader.hasEntryAfterSelection();
  const selectionIntent = isSelected ? selectionsReader.getSelectionIntent() : null;
  const isHoverDeleteEntriesButton =
    selectionIntent?.type === 'delete' && selectionIntent?.target === 'entries';

  const isFilterOrSortingActive = getIsFilterOrSortingActive(viewSettings);

  const canAskRovo =
    isDBAiFirstCreationEnabled &&
    canEditContent &&
    isSelected &&
    selectedEntriesCount > 0;
  const canInsertEntry = canEditContent && isSelected && selectedEntriesCount > 0;
  const canDuplicateEntry = canEditContent && isSelected && selectedEntriesCount > 0;
  const canDeleteEntry = canEditContent && isSelected && selectedEntriesCount > 0;
  const canCopyValue = isSelected && selectedEntriesCount === 1;
  const canMoveUp = canEditContent && hasEntryBeforeSelection;
  const canMoveDown = canEditContent && hasEntryAfterSelection;
  const isReorderDisabled = isFilterOrSortingActive;

  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const client = useAnalytics();

  const { isPanelVisible, getData } = usePanelState();
  const { showPanel, hidePanel } = usePanel();
  const { entryId: panelEntryId } = getData('EntryViewPanel') || {};

  const handleCollapseEntry = useCallback(() => {
    hidePanel();
  }, [hidePanel]);

  const handleExpandEntry = useCallback(() => {
    showPanel('EntryViewPanel', {
      entryId,
      isReadonly: !canEditContent,
    });
  }, [canEditContent, entryId, showPanel]);

  const menuData: MenuData = {
    type: 'entry',
    entryId,
  };
  const { isMenuOpen, toggleOpenMenu } = useContextualMenu(menuData);
  const prevIsSelected = useRef(isSelected);
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      toggleOpenMenu();
      if (!isSelected) {
        handleSelectEntry(e);
        prevIsSelected.current = true;
      } else {
        prevIsSelected.current = false;
      }
    },
    [handleSelectEntry, isSelected, toggleOpenMenu]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      const isMultiSelectOn = isModifierKeyPressed(e, ['shiftKey']);
      if (isSelected && !prevIsSelected.current && !isMenuOpen) {
        // remove selection if the user clicks on the select button again
        handleSelectEntry(e);
      } else if (!isMultiSelectOn && isPanelVisible('EntryViewPanel')) {
        // open the entry dialog only if the dialog has already been opened from another entry
        handleExpandEntry();
      }
    },
    [handleExpandEntry, handleSelectEntry, isMenuOpen, isPanelVisible, isSelected]
  );

  const handleRightClick = (e: React.MouseEvent) => e.preventDefault();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return;
      }

      e.preventDefault();
      if (!isMenuOpen) {
        toggleOpenMenu();
      }

      if (!isSelected) {
        handleSelectEntry(e);
      }
    },
    [handleSelectEntry, isMenuOpen, isSelected, toggleOpenMenu]
  );

  const TriggerContainer = inlineTrigger
    ? InlineSelectButtonContainer
    : RowSelectButtonContainer;

  const defaultTrigger = useComponent(
    ({ ref, ...triggerProps }: TriggerProps) => (
      <TriggerContainer
        className={inlineTrigger ? undefined : IGNORED_TABLE_ELEMENT_CLASS}
        {...hoverProps}
      >
        <DragHandlerWrapper
          ref={ref}
          type="button"
          className={TABLE_CLASSNAMES.SELECT_BUTTON}
          isActive={isMenuOpen && isSelected}
          isDeletionActive={isHoverDeleteEntriesButton}
          data-interactive
          data-testid="row-select-button"
          {...triggerProps}
          aria-label={formatMessage(messages['table-layout.select-row-label'])}
          aria-expanded={isMenuOpen}
          aria-haspopup="menu"
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onContextMenu={handleRightClick}
          onKeyDown={handleKeyDown}
        >
          <div className={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}>
            <ActiveVerticalDragHandlerIcon
              label={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}
            />
          </div>
          <div className={TABLE_CLASSNAMES.DRAG_HANDLER_ICON}>
            <VerticalDragHandlerIcon label={TABLE_CLASSNAMES.DRAG_HANDLER_ICON} />
          </div>
        </DragHandlerWrapper>
      </TriggerContainer>
    ),
    [
      formatMessage,
      handleMouseDown,
      handleMouseUp,
      handleKeyDown,
      hoverProps,
      inlineTrigger,
      isMenuOpen,
      isSelected,
      isHoverDeleteEntriesButton,
      TriggerContainer,
    ]
  );

  const triggerComp = trigger ?? defaultTrigger;

  const [isCopied, setIsCopied] = useState(false);

  const handleCopyEntryLink = useCallback(() => {
    const entryLinkCopyUrl = getDatabaseLinkUrlWithQueryParams({
      baseUrl: baseUrl,
      databasePath: databasePath,
      contentId: contentId,
      entryId: entryId,
      viewId: currentViewId,
    });

    copyToClipboard(entryLinkCopyUrl.toString(), 'text/plain');
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 3000);
  }, [baseUrl, contentId, currentViewId, databasePath, entryId]);

  const handleInsertEntries = useCallback(
    async (position: Position) => {
      await dispatch(
        doSelectNewEntries(contentId, () =>
          dispatch(doInsertEntries(contentId, viewSettings, position === Position.BEFORE))
        )
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId, dispatch, viewSettings]
  );

  const handleDuplicateEntries = useCallback(() => {
    dispatch(
      doSelectNewEntries(contentId, () => dispatch(doDuplicateEntireEntries(contentId)))
    );
  }, [dispatch, contentId]);

  const handleClearEntries = useCallback(() => {
    dispatch(doClearEntireEntries(contentId));
  }, [contentId, dispatch]);

  const handleDeleteEntries = useCallback(() => {
    if (getService('SelectionsReader').isEntryInSelection(panelEntryId)) {
      hidePanel();
    }

    dispatch(doDeleteEntries(contentId));
    selectionsActions.clearSelectionIntent();
    void sendDeleteEntryEvent(
      client,
      SendDatabaseDeleteEntryEventSources.ENTRY_CONTEXTUAL_MENU,
      store.getState(),
      contentId
    );
  }, [store, contentId, panelEntryId, dispatch, selectionsActions, client, hidePanel]);

  const handleMoveEntries = useCallback(
    (position: Position) => {
      dispatch(doMoveEntries(contentId, position === Position.BEFORE));
    },
    [contentId, dispatch]
  );

  const RovoElemBefore = () => {
    useEffect(() => {
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: true,
      });
    }, []);
    return <AskRovoIcon height={16} width={16} noPadding />;
  };

  const askRovoMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.ASK_ROVO,
      content: formatMessage(messages['value-actions.ask-rovo']),
      onClick: () =>
        openInlineRovoPrompt(
          getInlineRovoPromptTriggerSourceForViewType(viewSettings.layout.type)
        ),
      closeOnClick: true,
      elemBefore: <RovoElemBefore />,
    }),
    [formatMessage, viewSettings.layout.type]
  );

  const expandEntryMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.EXPAND_ENTRY,
      content: formatMessage(messages['entry-actions.expand']),
      onClick: handleExpandEntry,
      closeOnClick: true,
      elemBefore: <ExpandIcon label="" />,
    }),
    [formatMessage, handleExpandEntry]
  );

  const collapseEntryMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.COLLAPSE_ENTRY,
      content: formatMessage(messages['entry-actions.collapse']),
      onClick: handleCollapseEntry,
      closeOnClick: true,
      elemBefore: <CollapseIcon label="" />,
    }),
    [formatMessage, handleCollapseEntry]
  );

  const copyEntryLinkMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.COPY_ENTRY_LINK,
      content: formatMessage(messages['entry-actions.copy-link']),
      onClick: handleCopyEntryLink,
      tooltipDescription: isCopied
        ? formatMessage(messages['copy-link.tooltip'])
        : undefined,
      tooltipPosition: 'right',
      elemBefore: <LinkIcon label="" />,
    }),
    [formatMessage, handleCopyEntryLink, isCopied]
  );

  const addEntryAboveMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_ABOVE,
      content: formatMessage(messages['entry-actions.add-above']),
      onClick: () => handleInsertEntries(Position.BEFORE),
      closeOnClick: true,
      shortcut: SHORTCUTS.ADD_ENTRY_ABOVE,
      elemBefore: <InsertRowAboveIcon label="" />,
    }),
    [formatMessage, handleInsertEntries]
  );

  const addEntryBelowMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.ADD_ENTRY_BELOW,
      content: formatMessage(messages['entry-actions.add-below']),
      onClick: () => handleInsertEntries(Position.AFTER),
      closeOnClick: true,
      shortcut: SHORTCUTS.ADD_ENTRY_BELOW,
      elemBefore: <InsertRowBelowIcon label="" />,
    }),
    [formatMessage, handleInsertEntries]
  );

  const duplicateEntriesMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.DUPLICATE_ENTRIES,
      content: formatMessage(messages['entry-actions.duplicate-entry'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: handleDuplicateEntries,
      closeOnClick: true,
      shortcut: SHORTCUTS.DUPLICATE,
      elemBefore: <CopyIcon label="" />,
    }),
    [formatMessage, handleDuplicateEntries, selectedEntriesCount]
  );

  const clearCellsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.CLEAR_CELLS,
      content: formatMessage(messages['value-actions.clear-cells'], { valuesCount: 2 }),
      onClick: handleClearEntries,
      closeOnClick: true,
      shortcut: SHORTCUTS.CLEAR,
      elemBefore: <CrossCircleIcon label="" />,
    }),
    [formatMessage, handleClearEntries]
  );

  const onHoverDeleteFieldButton = useCallback(() => {
    selectionsActions.setSelectionIntent({ type: 'delete', target: 'entries' });
  }, [selectionsActions]);

  const onLeaveDeleteFieldButton = useCallback(() => {
    selectionsActions.clearSelectionIntent();
  }, [selectionsActions]);

  const deleteEntriesMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.DELETE_ENTRIES,
      content: formatMessage(messages['entry-actions.delete'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: handleDeleteEntries,
      onMouseEnter: onHoverDeleteFieldButton,
      onMouseLeave: onLeaveDeleteFieldButton,
      closeOnClick: true,
      shortcut: SHORTCUTS.DELETE,
      elemBefore: (
        <DeleteIcon
          label=""
          color={
            isHoverDeleteEntriesButton
              ? token('color.icon.danger', colors.R500)
              : undefined
          }
        />
      ),
    }),
    [
      formatMessage,
      handleDeleteEntries,
      isHoverDeleteEntriesButton,
      onHoverDeleteFieldButton,
      onLeaveDeleteFieldButton,
      selectedEntriesCount,
    ]
  );

  const moveEntriesUpMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_UP,
      content: formatMessage(messages['entry-actions.move-up'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: () => handleMoveEntries(Position.BEFORE),
      closeOnClick: true,
      isDisabled: isReorderDisabled,
      shortcut: SHORTCUTS.MOVE_ENTRIES_UP,
      elemBefore: <ArrowUpIcon label="" />,
    }),
    [formatMessage, handleMoveEntries, isReorderDisabled, selectedEntriesCount]
  );

  const moveEntriesDownMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_ENTRY_MENU_ACTIONS.MOVE_ENTRIES_DOWN,
      content: formatMessage(messages['entry-actions.move-down'], {
        entryCount: selectedEntriesCount,
      }),
      onClick: () => handleMoveEntries(Position.AFTER),
      closeOnClick: true,
      isDisabled: isReorderDisabled,
      shortcut: SHORTCUTS.MOVE_ENTRIES_DOWN,
      elemBefore: <ArrowDownIcon label="" />,
    }),
    [formatMessage, handleMoveEntries, isReorderDisabled, selectedEntriesCount]
  );
  const menuItems = useMemo(() => {
    const isEntryPanelActive = isPanelVisible('EntryViewPanel');
    const firstGroup: MenuItem[] = [];
    const secondGroup: MenuItem[] = [];
    const thirdGroup: MenuItem[] = [];
    const fourthGroup: MenuItem[] = [];

    if (
      canAskRovo &&
      getExperimentValue('databasesAIFC', 'enabled', {
        fireExperimentExposure: false,
      })
    ) {
      firstGroup.push(askRovoMenuItem);
    }
    if (oneSelectedEntry && !isEntryPanelActive) {
      secondGroup.push(expandEntryMenuItem);
    }
    if (oneSelectedEntry && isEntryPanelActive) {
      secondGroup.push(collapseEntryMenuItem);
    }
    if (canCopyValue) {
      secondGroup.push(copyEntryLinkMenuItem);
    }
    if (canInsertEntry) {
      thirdGroup.push(addEntryAboveMenuItem);
      thirdGroup.push(addEntryBelowMenuItem);
    }
    if (canDuplicateEntry) {
      thirdGroup.push(duplicateEntriesMenuItem);
    }
    if (canDeleteEntry) {
      thirdGroup.push(clearCellsMenuItem);
      thirdGroup.push(deleteEntriesMenuItem);
    }
    if (canMoveUp) {
      fourthGroup.push(moveEntriesUpMenuItem);
    }
    if (canMoveDown) {
      fourthGroup.push(moveEntriesDownMenuItem);
    }
    return [
      { items: firstGroup },
      { items: secondGroup },
      { items: thirdGroup },
      { items: fourthGroup },
    ];
  }, [
    askRovoMenuItem,
    canMoveDown,
    canMoveUp,
    moveEntriesDownMenuItem,
    moveEntriesUpMenuItem,
    addEntryAboveMenuItem,
    addEntryBelowMenuItem,
    canAskRovo,
    canCopyValue,
    canDeleteEntry,
    canDuplicateEntry,
    canInsertEntry,
    clearCellsMenuItem,
    collapseEntryMenuItem,
    copyEntryLinkMenuItem,
    deleteEntriesMenuItem,
    duplicateEntriesMenuItem,
    expandEntryMenuItem,
    isPanelVisible,
    oneSelectedEntry,
  ]);

  useKeyboardShortcut(
    isMenuOpen && canInsertEntry ? SHORTCUTS.ADD_ENTRY_ABOVE.keyboardShortCut : undefined,
    () => handleInsertEntries(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canInsertEntry ? SHORTCUTS.ADD_ENTRY_BELOW.keyboardShortCut : undefined,
    () => handleInsertEntries(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canDuplicateEntry ? SHORTCUTS.DUPLICATE.keyboardShortCut : undefined,
    handleDuplicateEntries
  );
  // Suppress CLEAR when the selections engine is active — it owns Backspace
  // for all modes to avoid duplicate dispatches and broken undo.
  useKeyboardShortcut(
    isMenuOpen && canDeleteEntry && !isSelectionsPerformanceEnabled
      ? SHORTCUTS.CLEAR.keyboardShortCut
      : undefined,
    handleClearEntries
  );
  useKeyboardShortcut(
    isMenuOpen && canDeleteEntry ? SHORTCUTS.DELETE.keyboardShortCut : undefined,
    handleDeleteEntries
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveUp ? SHORTCUTS.MOVE_ENTRIES_UP.keyboardShortCut : undefined,
    () => handleMoveEntries(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveDown ? SHORTCUTS.MOVE_ENTRIES_DOWN.keyboardShortCut : undefined,
    () => handleMoveEntries(Position.AFTER)
  );

  return (
    <ContextualMenu
      menuData={menuData}
      items={menuItems}
      trigger={triggerComp}
      isActionBarEnabled={isActionBarEnabled}
    />
  );
};

function getInlineRovoPromptTriggerSourceForViewType(
  viewType: DatabaseLayoutType
): InlineRovoPromptTriggerSource {
  switch (viewType) {
    case DatabaseLayoutType.BOARD:
      return InlineRovoPromptTriggerSource.BoardEntry;
    case DatabaseLayoutType.GALLERY:
      return InlineRovoPromptTriggerSource.GalleryEntry;
    case DatabaseLayoutType.TABLE:
    default:
      return InlineRovoPromptTriggerSource.TableEntry;
  }
}

export default SelectEntryMenu;
